// ---------------------------------------------------------------------------
// OfferCard Renderer (editor key: OfferCard)
// ---------------------------------------------------------------------------
// Commerce primitive — published version of the editor `OfferCard` block.
// The editor preview renders a styled "limited time offer" card with badge,
// pricing, and a single CTA. Until now publishing dropped the block because
// no canonical renderer existed. This renderer emits static, JS-free markup
// with `ease-offer-card__*` class hooks so the published page can be styled
// from the theme stylesheet, and routes the CTA URL through `url()` so
// `javascript:` payloads cannot escape into the page.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface OfferCardProps {
  badge?: string;
  heading?: string;
  description?: string;
  originalPrice?: string;
  offerPrice?: string;
  ctaText?: string;
  ctaUrl?: string;
}

function str(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as OfferCardProps;

  const heading = str(p.heading);
  const offerPrice = str(p.offerPrice);
  if (!heading && !offerPrice) {
    return "<!-- offer-card missing heading and offerPrice -->";
  }

  const badge = str(p.badge);
  const description = str(p.description);
  const originalPrice = str(p.originalPrice);
  const ctaText = str(p.ctaText);
  const ctaUrl = str(p.ctaUrl);

  const badgeHtml = badge
    ? `\n    <span class="ease-offer-card__badge">${text(badge)}</span>`
    : "";
  const headingHtml = heading
    ? `\n    <h3 class="ease-offer-card__heading">${text(heading)}</h3>`
    : "";
  const descHtml = description
    ? `\n    <p class="ease-offer-card__description">${text(description)}</p>`
    : "";
  const originalHtml = originalPrice
    ? `\n      <span class="ease-offer-card__original-price"><s>${text(originalPrice)}</s></span>`
    : "";
  const offerHtml = offerPrice
    ? `\n      <span class="ease-offer-card__offer-price">${text(offerPrice)}</span>`
    : "";
  const priceBlock = originalPrice || offerPrice
    ? `\n    <p class="ease-offer-card__prices">${originalHtml}${offerHtml}\n    </p>`
    : "";
  const ctaHtml = ctaText
    ? `\n    <a class="ease-offer-card__cta ease-btn ease-btn-primary" href="${url(ctaUrl || "#")}">${text(ctaText)}</a>`
    : "";

  return `<section class="ease-section ease-offer-card" aria-label="${attr(heading || "Special offer")}">
  <div class="ease-offer-card__frame">${badgeHtml}${headingHtml}${descHtml}${priceBlock}${ctaHtml}
  </div>
</section>`;
}

registerRenderer("offer-card", { render });
